/**
 * Consent utilities — thin helpers over the ConsentManager (domain/consent.ts).
 *
 * The consent flow is event-driven: domain/consent.ts dispatches a
 * `consent-granted` CustomEvent (detail = ConsentPreferences) whenever a
 * decision is made or re-dispatched for a returning visitor. waitForConsent()
 * below is the single subscription point used by main.ts to gate all tracking.
 */
import { consentManager } from '../domain/consent';
export { showConsentSettings } from '../domain/consent';

export interface ConsentDecision {
  analytics: boolean;
  marketing: boolean;
}

/**
 * Check if user has granted consent for analytics
 */
export function hasAnalyticsConsent(): boolean {
  return consentManager.hasConsent('analytics');
}

/**
 * Check if user has granted consent for marketing
 */
export function hasMarketingConsent(): boolean {
  return consentManager.hasConsent('marketing');
}

/**
 * Wait for a consent decision and then initialize services.
 *
 * Handles both orderings:
 * - decision arrives later (banner click, first-visit implied consent after
 *   geo) → delivered via the `consent-granted` event;
 * - decision already existed before this listener registered (stored decision
 *   fast path in ConsentManager.initialize()) → delivered synchronously below.
 * Identical consecutive deliveries are deduped so the two paths never
 * double-initialize the vendors.
 */
export function waitForConsent(callback: (consent: ConsentDecision) => void): void {
  let lastDelivered: string | null = null;

  const deliver = (prefs: { analytics?: boolean; marketing?: boolean }) => {
    const decision: ConsentDecision = {
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing
    };
    const key = JSON.stringify(decision);
    if (key === lastDelivered) return; // dedupe event + sync double-delivery
    lastDelivered = key;
    callback(decision);
  };

  // Listen for consent granted event
  window.addEventListener('consent-granted', ((event: CustomEvent) => {
    deliver(event.detail || {});
  }) as EventListener);

  // Check if a decision was already made before this listener was added
  const status = consentManager.getConsentStatus();
  if (status && status.status !== 'pending') {
    deliver({
      analytics: consentManager.hasConsent('analytics'),
      marketing: consentManager.hasConsent('marketing')
    });
  }
}

/**
 * Get user's country code from geo detection
 */
export function getUserCountry(): string | undefined {
  return window.USER_COUNTRY;
}

/**
 * Check if user is in a region that requires explicit consent
 */
export function isInStrictConsentRegion(): boolean {
  return consentManager.requiresStrictConsent();
}

/**
 * Revoke consent and show the banner again
 * Useful for privacy settings page
 */
export function revokeConsent(): void {
  consentManager.revokeConsent();
}
